/**
 * EmailsSection — Settings > Mailler paneli.
 *
 * Super admin'e uygulamanın gönderdiği tüm e-postaları listeler,
 * her biri için önizleme + test e-postası gönderme sağlar.
 *
 * Endpoints:
 *   GET  /api/emails/templates    → { templates, sendgrid_configured, default_test_recipient }
 *   POST /api/emails/test         → { template_id, to_email } → { delivery: {...} }
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Eye,
  Check,
  AlertTriangle,
  Loader2,
  Filter,
  Zap,
  Clock,
  Users as UsersIcon,
  ShieldCheck,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import EmailTemplateWizard from "./EmailTemplateWizard";
import BroadcastDialog from "./BroadcastDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const CATEGORY_LABELS = {
  user_lifecycle: "Kullanıcı",
  task: "Görev",
  notification: "Bildirim",
  digest: "Özet",
  billing: "Fatura",
};

const CATEGORY_STYLES = {
  user_lifecycle: "bg-violet-50 text-violet-700 ring-violet-200",
  task: "bg-blue-50 text-blue-700 ring-blue-200",
  notification: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  digest: "bg-amber-50 text-amber-700 ring-amber-200",
  billing: "bg-rose-50 text-rose-700 ring-rose-200",
};

export default function EmailsSection() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [sendgridOk, setSendgridOk] = useState(false);
  const [defaultRecipient, setDefaultRecipient] = useState("mbucak@gmail.com");
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [preview, setPreview] = useState(null); // template being previewed
  const [testTarget, setTestTarget] = useState(null); // template being test-sent
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);
  const [lastSendResult, setLastSendResult] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [wizardEditing, setWizardEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // custom template to delete

  const reload = async () => {
    try {
      const { data } = await api.get("/emails/templates");
      setTemplates(data.templates || []);
      setSendgridOk(!!data.sendgrid_configured);
      setDefaultRecipient(data.default_test_recipient || "mbucak@gmail.com");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Şablonlar yüklenemedi",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    const set = new Set(templates.map((t) => t.category));
    return ["all", ...Array.from(set)];
  }, [templates]);

  const visible = useMemo(() => {
    if (categoryFilter === "all") return templates;
    return templates.filter((t) => t.category === categoryFilter);
  }, [templates, categoryFilter]);

  const openTest = (tpl) => {
    setTestTarget(tpl);
    setRecipient(defaultRecipient);
    setLastSendResult(null);
  };

  const closeTest = () => {
    if (sending) return;
    setTestTarget(null);
    setRecipient("");
    setLastSendResult(null);
  };

  const sendTest = async () => {
    if (!testTarget) return;
    const email = (recipient || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Geçerli bir e-posta adresi gir");
      return;
    }
    setSending(true);
    setLastSendResult(null);
    try {
      // Custom templates route through /emails/custom/{id}/test so the
      // subject/body variables get substituted from the persisted row.
      // Built-in ones stay on /emails/test.
      const url = testTarget.is_custom
        ? `/emails/custom/${testTarget.id}/test`
        : "/emails/test";
      const body = testTarget.is_custom
        ? { to_email: email }
        : { template_id: testTarget.id, to_email: email };
      const { data } = await api.post(url, body);
      setLastSendResult(data);
      if (data?.delivery?.delivered) {
        toast.success(`Test e-postası ${email} adresine gönderildi`);
      } else if (data?.delivery?.provider === "outbox") {
        toast.info("SendGrid yapılandırılmamış — mail sadece outbox'a düştü");
      } else {
        toast.error(
          `Gönderim başarısız: ${data?.delivery?.error || "bilinmeyen hata"}`,
        );
      }
    } catch (err) {
      const msg =
        formatApiErrorDetail(err.response?.data?.detail) ||
        "Test gönderilemedi";
      toast.error(msg);
      setLastSendResult({ error: msg });
    } finally {
      setSending(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/emails/custom/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.name}" silindi`);
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi",
      );
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-xl border border-zinc-200 bg-white p-10 text-center"
        data-testid="settings-emails-loading"
      >
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" />
        <p className="mt-2 text-sm text-zinc-500">Şablonlar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="settings-emails-section">
      <div className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
            <Mail className="h-5 w-5 text-violet-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-semibold text-zinc-900">
              Mail Şablonları
            </h2>
            <p className="mt-1 text-sm text-zinc-600">
              Serpix&apos;in gönderdiği tüm e-postalar burada listelenir. Her
              şablonun içeriğini önizleyebilir ve dilediğin bir adrese test
              e-postası gönderebilirsin. Varsayılan alıcı:{" "}
              <b className="text-zinc-900">{defaultRecipient}</b>.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setBroadcastOpen(true)}
              data-testid="emails-broadcast-btn"
              className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              <Send className="h-4 w-4" /> Duyuru Gönder
            </Button>
            <Button
              type="button"
              onClick={() => {
                setWizardEditing(null);
                setWizardOpen(true);
              }}
              data-testid="emails-new-template-btn"
              className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
            >
              <Plus className="h-4 w-4" /> Yeni şablon
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div
            className={[
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ring-1",
              sendgridOk
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : "bg-amber-50 text-amber-700 ring-amber-200",
            ].join(" ")}
            data-testid="emails-sendgrid-status"
          >
            {sendgridOk ? (
              <>
                <Check className="h-3 w-3" /> SendGrid bağlı — mailler gerçekten
                gönderiliyor
              </>
            ) : (
              <>
                <AlertTriangle className="h-3 w-3" /> SendGrid yapılandırılmadı
                — testler sadece outbox&apos;a düşer
              </>
            )}
          </div>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-zinc-400" />
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategoryFilter(cat)}
            data-testid={`emails-filter-${cat}`}
            className={[
              "rounded-full px-3 py-1 text-xs font-medium transition-colors",
              categoryFilter === cat
                ? "bg-zinc-900 text-white"
                : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
            ].join(" ")}
          >
            {cat === "all" ? "Tümü" : CATEGORY_LABELS[cat] || cat}
            <span className="ml-1.5 opacity-70">
              (
              {cat === "all"
                ? templates.length
                : templates.filter((t) => t.category === cat).length}
              )
            </span>
          </button>
        ))}
      </div>

      {/* Template list */}
      <ul
        className="grid grid-cols-1 gap-3 xl:grid-cols-2"
        data-testid="emails-template-list"
      >
        {visible.map((tpl) => (
          <li
            key={tpl.id}
            data-testid={`email-template-${tpl.id}`}
            className={[
              "flex flex-col gap-3 rounded-xl border bg-white p-5 transition-shadow hover:shadow-sm",
              tpl.is_custom ? "border-violet-200 ring-1 ring-violet-100" : "border-zinc-200",
            ].join(" ")}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-zinc-900">
                    {tpl.name}
                  </h3>
                  {tpl.is_custom && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700"
                      data-testid={`email-custom-badge-${tpl.id}`}
                    >
                      Özel
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-[11px] font-mono text-zinc-400">
                  {tpl.id}
                </p>
              </div>
              <span
                className={[
                  "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
                  CATEGORY_STYLES[tpl.category] || CATEGORY_STYLES.notification,
                ].join(" ")}
              >
                {CATEGORY_LABELS[tpl.category] || tpl.category}
              </span>
            </div>

            <p className="text-xs leading-relaxed text-zinc-600">
              {tpl.description}
            </p>

            {/* Metadata strip: trigger + frequency + recipients */}
            <div className="grid grid-cols-1 gap-2 rounded-md border border-zinc-100 bg-zinc-50/70 p-3 text-[11px]">
              <div className="flex items-start gap-2">
                <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                <div className="min-w-0">
                  <div className="font-semibold uppercase tracking-wider text-zinc-500">
                    Tetikleyici
                  </div>
                  <div
                    className="mt-0.5 break-words font-mono text-[10.5px] text-zinc-800"
                    data-testid={`email-trigger-${tpl.id}`}
                  >
                    {tpl.trigger}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                <div className="min-w-0">
                  <div className="font-semibold uppercase tracking-wider text-zinc-500">
                    Sıklık
                  </div>
                  <div
                    className="mt-0.5 text-zinc-800"
                    data-testid={`email-frequency-${tpl.id}`}
                  >
                    {tpl.frequency || "—"}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <UsersIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                <div className="min-w-0">
                  <div className="font-semibold uppercase tracking-wider text-zinc-500">
                    Alıcı(lar)
                  </div>
                  <div
                    className="mt-0.5 text-zinc-800"
                    data-testid={`email-recipients-${tpl.id}`}
                  >
                    {tpl.recipients || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Rules — compact bullet list. When more than 2, remaining
                shown as a "+N kural" pill that opens the preview. */}
            {Array.isArray(tpl.rules) && tpl.rules.length > 0 && (
              <div
                className="rounded-md border border-amber-100 bg-amber-50/60 p-3 text-[11px] text-amber-900"
                data-testid={`email-rules-${tpl.id}`}
              >
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  <ShieldCheck className="h-3 w-3" />
                  Gönderim kuralları
                </div>
                <ul className="space-y-1">
                  {tpl.rules.slice(0, 2).map((rule, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-600" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
                {tpl.rules.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setPreview(tpl)}
                    data-testid={`email-rules-more-${tpl.id}`}
                    className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 hover:text-amber-900 hover:underline"
                  >
                    +{tpl.rules.length - 2} kural daha →
                  </button>
                )}
              </div>
            )}

            <div className="rounded-md bg-zinc-50 px-3 py-2 text-[11px]">
              <div className="text-zinc-400">Konu</div>
              <div className="mt-0.5 truncate font-medium text-zinc-800">
                {tpl.subject}
              </div>
            </div>

            <div className="mt-auto flex flex-wrap gap-2 border-t border-zinc-100 pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPreview(tpl)}
                data-testid={`email-preview-${tpl.id}`}
                className="flex-1 gap-1.5"
              >
                <Eye className="h-3.5 w-3.5" /> Önizle
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => openTest(tpl)}
                data-testid={`email-test-${tpl.id}`}
                className="flex-1 gap-1.5 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                <Send className="h-3.5 w-3.5" /> Test gönder
              </Button>
              {tpl.is_custom ? (
                <div className="flex w-full gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setWizardEditing(tpl);
                      setWizardOpen(true);
                    }}
                    data-testid={`email-edit-${tpl.id}`}
                    className="flex-1 gap-1.5"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Düzenle
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTarget(tpl)}
                    data-testid={`email-delete-${tpl.id}`}
                    className="flex-1 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Sil
                  </Button>
                </div>
              ) : (
                // Built-in template — user can "customize" it (opens the
                // wizard prefilled and saves as a NEW custom template).
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setWizardEditing({ ...tpl, _cloneFromBuiltin: true });
                    setWizardOpen(true);
                  }}
                  data-testid={`email-edit-${tpl.id}`}
                  className="w-full gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50"
                >
                  <Pencil className="h-3.5 w-3.5" /> Şablonu özelleştir
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Preview dialog */}
      <Dialog
        open={!!preview}
        onOpenChange={(o) => !o && setPreview(null)}
      >
        <DialogContent
          className="max-w-2xl"
          data-testid="email-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>{preview?.name}</DialogTitle>
            <DialogDescription>
              {preview?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Metadata block */}
            <div className="grid grid-cols-1 gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs sm:grid-cols-2">
              <div className="flex items-start gap-2">
                <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Tetikleyici
                  </div>
                  <div className="mt-0.5 break-words font-mono text-[11px] text-zinc-800">
                    {preview?.trigger}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Sıklık
                  </div>
                  <div className="mt-0.5 text-zinc-800">
                    {preview?.frequency || "—"}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:col-span-2">
                <UsersIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Alıcı(lar)
                  </div>
                  <div className="mt-0.5 text-zinc-800">
                    {preview?.recipients || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* All rules */}
            {Array.isArray(preview?.rules) && preview.rules.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50/70 p-3"
                data-testid="email-preview-rules"
              >
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                  <ShieldCheck className="h-3 w-3" />
                  Gönderim kuralları
                </div>
                <ul className="space-y-1.5 text-xs text-amber-900">
                  {preview.rules.map((rule, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-700" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Konu
              </Label>
              <div className="mt-1 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-900">
                {preview?.subject}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
                Gövde (örnek verilerle)
              </Label>
              <pre
                className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-800"
                data-testid="email-preview-body"
              >
                {preview?.body_preview}
              </pre>
            </div>
            {preview?.cta_url && (
              <div className="rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-800">
                <b>CTA butonu:</b> {preview.cta_label} → {preview.cta_url}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPreview(null)}
              data-testid="email-preview-close"
            >
              Kapat
            </Button>
            <Button
              type="button"
              onClick={() => {
                openTest(preview);
                setPreview(null);
              }}
              data-testid="email-preview-open-test"
              className="gap-1.5 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              <Send className="h-3.5 w-3.5" /> Bu şablonu test gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test-send dialog */}
      <Dialog
        open={!!testTarget}
        onOpenChange={(o) => !o && closeTest()}
      >
        <DialogContent
          className="max-w-md"
          data-testid="email-test-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-violet-600" />
              Test e-postası gönder
            </DialogTitle>
            <DialogDescription>
              <b>{testTarget?.name}</b> şablonu, seçtiğin adrese örnek
              verilerle bir kez gönderilecek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label
                htmlFor="test-recipient"
                className="text-[11px] uppercase tracking-wider text-zinc-500"
              >
                Alıcı e-posta
              </Label>
              <Input
                id="test-recipient"
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={defaultRecipient}
                disabled={sending}
                data-testid="email-test-recipient-input"
                className="mt-1"
                autoFocus
              />
              {defaultRecipient && recipient !== defaultRecipient && (
                <button
                  type="button"
                  onClick={() => setRecipient(defaultRecipient)}
                  data-testid="email-test-recipient-reset"
                  className="mt-1 text-[11px] font-medium text-violet-600 hover:underline"
                >
                  Varsayılana geri dön ({defaultRecipient})
                </button>
              )}
            </div>

            {lastSendResult && (
              <div
                className={[
                  "rounded-md px-3 py-2 text-xs",
                  lastSendResult?.delivery?.delivered
                    ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                    : "bg-rose-50 text-rose-800 ring-1 ring-rose-200",
                ].join(" ")}
                data-testid="email-test-result"
              >
                {lastSendResult?.delivery?.delivered ? (
                  <>
                    <b>✅ Gönderildi.</b> Provider:{" "}
                    <code>{lastSendResult.delivery.provider}</code> · Durum:{" "}
                    <code>{lastSendResult.delivery.status_code}</code>
                  </>
                ) : lastSendResult?.delivery?.provider === "outbox" ? (
                  <>
                    <b>⚠️ Outbox&apos;a düştü.</b> SendGrid yapılandırılmadığı için
                    mail servis dışına çıkmadı — /app/email-outbox sayfasından
                    içeriği görebilirsin.
                  </>
                ) : (
                  <>
                    <b>❌ Hata:</b>{" "}
                    {lastSendResult?.delivery?.error ||
                      lastSendResult?.error ||
                      "Bilinmeyen bir hata."}
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeTest}
              disabled={sending}
              data-testid="email-test-cancel"
            >
              Kapat
            </Button>
            <Button
              type="button"
              onClick={sendTest}
              disabled={sending || !recipient}
              data-testid="email-test-send-btn"
              className="gap-1.5 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {sending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Gönderiliyor
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" /> Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* + Yeni şablon / Düzenle sihirbazı */}
      <EmailTemplateWizard
        open={wizardOpen}
        editing={wizardEditing}
        defaultTestRecipient={defaultRecipient}
        onClose={() => {
          setWizardOpen(false);
          setWizardEditing(null);
        }}
        onSaved={() => {
          setWizardOpen(false);
          setWizardEditing(null);
          reload();
        }}
      />

      {/* Duyuru gönderimi — tüm aktif kullanıcılara */}
      <BroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="email-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bu şablonu silmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{deleteTarget?.name}</b> şablonu kalıcı olarak silinecek.
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-delete-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doDelete();
              }}
              data-testid="email-delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Kalıcı olarak sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
